import React, { useMemo } from 'react';
import { DatePicker, Button, Input } from '@douyinfe/semi-ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';

const UsageChart = ({ 
    logs, 
    selectedDate, 
    onDateChange, 
    dollarCost, 
    setDollarCost, 
    showRMB, 
    setShowRMB 
}) => {
    // 每日消耗数据计算
    const dailyQuotaMap = useMemo(() => {
        const map = {};
        if (!logs) return map;
        logs.forEach(log => {
            if (log.type === 0 || log.type === 2) {
                const date = new Date(log.created_at * 1000);
                const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
                map[dateKey] = (map[dateKey] || 0) + (log.quota || 0);
            }
        });
        return map;
    }, [logs]);

    // 每小时消耗数据计算
    const { hourlyData, models } = useMemo(() => {
        if (!logs) return { hourlyData: [], models: [] };

        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);

        const modelSet = new Set();
        const hourlyMap = {};
        for (let i = 0; i < 24; i++) {
            hourlyMap[i] = { hour: `${i}:00`, total: 0 };
        }

        logs.forEach(log => {
            const logTime = new Date(log.created_at * 1000);
            if (logTime >= startOfDay && logTime <= endOfDay) {
                const hour = logTime.getHours();
                const model = log.model_name || '未知模型';
                modelSet.add(model);

                if (!hourlyMap[hour][model]) {
                    hourlyMap[hour][model] = 0;
                }
                hourlyMap[hour][model] += log.quota || 0;
                hourlyMap[hour].total += log.quota || 0;
            }
        });

        return {
            hourlyData: Object.values(hourlyMap),
            models: Array.from(modelSet)
        };
    }, [logs, selectedDate]);

    const exchangeRate = showRMB ? parseFloat(dollarCost || 0) : 0;
    const MODEL_COLORS = ['#0071e3', '#34c759', '#ff9500', '#ff3b30', '#af52de', '#5856d6', '#00c7be', '#ff2d55', '#a2845e', '#64d2ff'];

    // 自定义 Tooltip 组件 - 玻璃拟态风格
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(0,0,0,0.05)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    fontSize: '13px'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1d1d1f' }}>{label}</p>
                    {payload.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: entry.fill, marginRight: 8 }} />
                            <span style={{ color: '#86868b', marginRight: 8 }}>{entry.name}:</span>
                            <span style={{ fontWeight: 500, color: '#1d1d1f' }}>
                                {showRMB
                                    ? `￥${(entry.value / 500000 * exchangeRate).toFixed(4)}`
                                    : `$${(entry.value / 500000).toFixed(4)}`}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="apple-card" style={{ marginTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>每小时消耗统计</h3>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Input
                        value={dollarCost}
                        onChange={setDollarCost}
                        placeholder="每刀成本"
                        style={{ width: 120 }}
                        prefix="￥"
                    />
                    <Button
                        theme="solid"
                        type={showRMB ? 'tertiary' : 'primary'}
                        onClick={() => setShowRMB(!showRMB)}
                    >
                        {showRMB ? '显示美元' : '显示人民币'}
                    </Button>
                    <DatePicker
                        type="date"
                        value={selectedDate}
                        onChange={date => onDateChange(date || new Date())}
                        style={{ width: 200 }}
                        renderFullDate={(dayNumber, fullDate, dayStatus) => {
                            const date = new Date(fullDate);
                            const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
                            const quota = dailyQuotaMap[dateKey];
                            const hasQuota = quota && quota > 0;
                            const { isSelected, isToday } = dayStatus;
                            const quotaAmount = quota ? quota / 500000 : 0;
                            const displayAmount = showRMB
                                ? '￥' + (quotaAmount * exchangeRate).toFixed(2)
                                : '$' + quotaAmount.toFixed(2);

                            return (
                                <div style={{
                                    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center', borderRadius: 4,
                                    backgroundColor: isSelected ? '#0071e3' : hasQuota ? 'rgba(52, 199, 89, 0.1)' : 'transparent',
                                    color: isSelected ? '#fff' : 'inherit'
                                }}>
                                    <span style={{ fontWeight: isToday ? 700 : 400 }}>{dayNumber}</span>
                                    {hasQuota && <span style={{ fontSize: 9, color: isSelected ? '#fff' : '#34c759' }}>{displayAmount}</span>}
                                </div>
                            );
                        }}
                    />
                </div>
            </div>

            <ResponsiveContainer width="100%" height={360}>
                <BarChart data={hourlyData} margin={{ top: 20, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.04)" />
                    <XAxis
                        dataKey="hour"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#86868b' }}
                        dy={10}
                    />
                    <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12, fill: '#86868b' }}
                        tickFormatter={val => showRMB ? `￥${(val/500000*exchangeRate).toFixed(2)}` : `$${(val/500000).toFixed(2)}`}
                    />
                    <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                    <Legend
                        iconType="circle"
                        wrapperStyle={{ paddingTop: '20px' }}
                        formatter={(value) => <span style={{ color: '#666', fontSize: '13px', marginRight: '10px' }}>{value}</span>}
                    />
                    {models.map((model, index) => (
                        <Bar
                            key={model}
                            dataKey={model}
                            stackId="a"
                            fill={MODEL_COLORS[index % MODEL_COLORS.length]}
                            radius={index === models.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                            maxBarSize={50}
                            animationDuration={1500}
                            animationEasing="ease-out"
                        >
                            {index === models.length - 1 && (
                                <LabelList
                                    dataKey="total"
                                    position="top"
                                    formatter={(value) => value > 0
                                        ? (showRMB
                                            ? `￥${(value / 500000 * exchangeRate).toFixed(3)}`
                                            : `$${(value / 500000).toFixed(3)}`)
                                        : ''}
                                    style={{ fontSize: 11, fill: '#86868b', fontWeight: 500 }}
                                />
                            )}
                        </Bar>
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default UsageChart;

